/**
 * Patient Grid Component
 *
 * TanStack Table grid for quality measure patient drill-down
 * Full UserDataTable pattern with sorting, filtering, pagination, CSV export
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Download, ExternalLink, Mail, Search } from 'lucide-react'
import { askBulkQuestion } from '@/services/askQuestionService'
import type { PatientDrilldownItem } from '@/lib/equip-types'
import { getPatientGridColumns, getCSVExportColumns } from './PatientGridColumns'
import { equipTable } from '@/views/equip/equip-table-styles'
import { cn } from '@/lib/utils'

interface PatientGridProps {
  patients: PatientDrilldownItem[]
  measureName: string
  isLoading: boolean
  onPatientClick: (patient: PatientDrilldownItem) => void
  selectedPatientIds: string[]
  onSelectionChange: (ids: string[]) => void
  providerName?: string
  providerEmail?: string
  year: number
  quarter: number
  embedded?: boolean
}

export function PatientGrid({
  patients,
  measureName,
  isLoading,
  onPatientClick,
  selectedPatientIds,
  onSelectionChange,
  providerName,
  providerEmail,
  year,
  quarter,
  embedded = false,
}: PatientGridProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [showUnmetOnly, setShowUnmetOnly] = useState(true)
  const [selectedFacility, setSelectedFacility] = useState<string>('all')
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [showSearch, setShowSearch] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Column definitions (memoized, dynamic based on measure)
  const columns = useMemo(
    () => getPatientGridColumns(measureName),
    [measureName]
  )

  // ACO_VISITS uses cumulative YTD cutoff — show deferred (non-eligible) rows.
  // AWV is now cumulative YTD at the SP level: all returned patients are eligible,
  // so no deferred rows exist for AWV.
  const hasDeferredRows = measureName.toUpperCase() === 'ACO_VISITS'
  const nextQuarter = quarter < 4 ? quarter + 1 : null

  // Collapse source/join fan-out before filtering, counting, or exporting. Prefer
  // an eligible row, then a met row, when duplicate source rows disagree.
  const uniquePatients = useMemo(() => {
    const byPatient = new Map<string, PatientDrilldownItem>()
    for (const patient of patients) {
      const key = String(patient.patientId)
      const existing = byPatient.get(key)
      if (!existing) {
        byPatient.set(key, patient)
        continue
      }
      const existingRank = Number(existing.isEligible) * 2 + Number(existing.isMet)
      const patientRank = Number(patient.isEligible) * 2 + Number(patient.isMet)
      if (patientRank > existingRank) {
        byPatient.set(key, patient)
      }
    }
    return Array.from(byPatient.values())
  }, [patients])

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = hasDeferredRows
      ? uniquePatients // Show all rows (eligible + deferred) for cutoff-based measures
      : uniquePatients.filter((p) => p.isEligible) // Only show eligible patients

    // Keep unmet eligible rows + all deferred (non-eligible) rows
    if (showUnmetOnly) {
      filtered = filtered.filter((p) => !p.isMet || !p.isEligible)
    }

    // Filter by facility name
    if (selectedFacility !== 'all') {
      filtered = filtered.filter((p) => (p.facilityName || p.facilityId) === selectedFacility)
    }

    return filtered
  }, [uniquePatients, showUnmetOnly, selectedFacility, hasDeferredRows])

  // Dynamically size table to fill remaining viewport space (skip when embedded in accordion)
  useLayoutEffect(() => {
    if (embedded) return
    const el = scrollContainerRef.current
    if (!el) return
    const recalc = () => {
      const top = el.getBoundingClientRect().top
      // Reserve 30px below for card padding
      const maxH = Math.max(200, window.innerHeight - top - 30)
      el.style.maxHeight = `${maxH}px`
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [filteredData, embedded])

  // Table instance
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
      rowSelection,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  // Sync row selection with parent
  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows
    const ids = selectedRows.map((r) => String(r.original.patientId))
    if (JSON.stringify(ids) !== JSON.stringify(selectedPatientIds)) {
      onSelectionChange(ids)
    }
  }, [rowSelection, table, selectedPatientIds, onSelectionChange])

  // Calculate summary stats
  const eligibleCount = uniquePatients.filter((p) => p.isEligible).length
  const metCount = uniquePatients.filter((p) => p.isEligible && p.isMet).length
  const unmetCount = Math.max(0, eligibleCount - metCount)
  const rate = eligibleCount > 0 ? ((metCount / eligibleCount) * 100).toFixed(1) : '0.0'
  const deferredCount = hasDeferredRows ? uniquePatients.filter((p) => !p.isEligible).length : 0

  // Unique facilities for filter dropdown (use name, fallback to ID)
  const facilities = useMemo(() => {
    const unique = Array.from(new Set(uniquePatients.map((p) => p.facilityName || p.facilityId)))
    return unique.filter(Boolean).sort()
  }, [uniquePatients])

  // CSV Export handler
  const handleExportCSV = () => {
    const csvColumns = getCSVExportColumns(measureName)
    const rows = filteredData.map((row) =>
      csvColumns.map((col) => col.accessor(row))
    )
    const csvContent = [
      csvColumns.map((col) => col.header).join(','),
      ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${measureName}_patients.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Ask a Question bulk handler
  const handleAskQuestion = async () => {
    await askBulkQuestion(
      selectedPatientIds,
      'patient',
      {
        providerName: providerName || 'Unknown',
        providerEmail: providerEmail || '',
        year,
        quarter,
        measureName,
      }
    )
  }

  if (isLoading) {
    if (embedded) {
      return (
        <div className="py-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-8 w-full mb-2" />
          ))}
        </div>
      )
    }
    return (
      <Card>
        <CardHeader>
          <CardTitle>Patient Details - {measureName}</CardTitle>
        </CardHeader>
        <CardContent>
          <AnimatedTableWrapper>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full mb-2" />
            ))}
          </AnimatedTableWrapper>
        </CardContent>
      </Card>
    )
  }

  const gridContent = (
    <>
        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {/* Unmet/All Toggle */}
          <div className="flex gap-1">
            <Button
              variant={showUnmetOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowUnmetOnly(true)}
            >
              Show Unmet Only
            </Button>
            <Button
              variant={!showUnmetOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowUnmetOnly(false)}
            >
              Show All
            </Button>
          </div>

          {/* Facility Filter */}
          <Select value={selectedFacility} onValueChange={setSelectedFacility}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Facilities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Facilities</SelectItem>
              {facilities.map((f) => (
                <SelectItem key={f} value={f}>
                  {f}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          {!showSearch ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSearch(true)}
              className="px-3 bg-background"
            >
              <Search className="h-4 w-4" />
            </Button>
          ) : (
            <Input
              placeholder="Search..."
              className="w-40 md:w-56 bg-background"
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => {
                if (!globalFilter) setShowSearch(false)
              }}
              autoFocus
            />
          )}

          {/* CSV Export */}
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="bg-background">
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>

          {/* Ask a Question */}
          {selectedPatientIds.length > 0 && (
            <Button variant="default" size="sm" onClick={handleAskQuestion}>
              <Mail className="h-4 w-4 mr-1" />
              Ask a Question ({selectedPatientIds.length})
            </Button>
          )}
        </div>

        {/* Table */}
        {filteredData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No patients found for this measure
          </div>
        ) : (
          <div className={equipTable.wrapper}>
          <div ref={scrollContainerRef} className="overflow-auto">
            <AnimatedTableWrapper>
              <table className={equipTable.table}>
                <thead className={equipTable.thead}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className={equipTable.theadTr}>
                      {headerGroup.headers.map((header, headerIdx) => (
                        <th
                          key={header.id}
                          className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <AnimatedTableBody>
                  {table.getRowModel().rows.map((row, index) => {
                    const isDeferred = hasDeferredRows && !row.original.isEligible
                    return (
                    <AnimatedTableRow
                      key={row.id}
                      index={index}
                      className={cn(
                        `${equipTable.trClickable} ${equipTable.trAlt(index)} group`,
                        isDeferred && 'opacity-50'
                      )}
                    >
                      {row.getVisibleCells().map((cell, cellIdx) => (
                        <td
                          key={cell.id}
                          className={cn(
                            equipTable.td,
                            cellIdx === 0 && (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)
                          )}
                          onClick={() => {
                            if (cell.column.id !== 'select') {
                              onPatientClick(row.original)
                            }
                          }}
                        >
                          <span className="flex items-center gap-1">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            {cellIdx === row.getVisibleCells().length - 1 && isDeferred && nextQuarter && (
                              <Badge variant="outline" className="text-[10px] ml-auto flex-shrink-0 text-muted-foreground border-muted-foreground/30">
                                Deferred to Q{nextQuarter}
                              </Badge>
                            )}
                            {cellIdx === row.getVisibleCells().length - 1 && !isDeferred && (
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0" />
                            )}
                          </span>
                        </td>
                      ))}
                    </AnimatedTableRow>
                    )
                  })}
                </AnimatedTableBody>
              </table>
            </AnimatedTableWrapper>
          </div>
          </div>
        )}

    </>
  )

  if (embedded) {
    return <div className="space-y-4">{gridContent}</div>
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Patient Details - {measureName}</CardTitle>
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Badge variant="lightInfo">{eligibleCount} eligible</Badge>
              <Badge variant="lightSuccess">{metCount} met</Badge>
              <Badge variant="lightError">{unmetCount} unmet</Badge>
              <span className="font-semibold">{rate}%</span>
              {deferredCount > 0 && nextQuarter && (
                <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30">
                  {deferredCount} deferred to Q{nextQuarter}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {gridContent}
      </CardContent>
    </Card>
  )
}
